import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { NumericFormat as NumberFormat } from "react-number-format";
import Select from "react-select";

import CsLineIcons from "../../../cs-line-icons";
import ConditionalButton from "../../../layout/conditional-button";
import ConditionalDropdown from "../../../layout/conditional-dropdown";
import ErrorModal from "../../../layout/modal";
import DeleteModal from "../../../layout/modal";
import BusinessExpenseModal from "../business.expenses/modal";
import AccountEntries from "./entries";

import { isGuid } from "../../../utils/string";
import { withNavigation } from "../../../utils/wrapper";

import { send as pageInfo, loading } from "../../../actions/page";
import {
  get as getAccount,
  save as saveAccount,
  destroy as destroyAccount,
} from "../../../actions/accounts";

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE", "COGS"];
const ACCOUNT_PURPOSES = [
  "ACCOUNTS_RECEIVABLE",
  "LEGAL_FEE_REVENUE",
  "COST_REIMBURSEMENT_REVENUE",
  "OPERATING_CASH",
  "IOLTA_TRUST_CASH",
  "CLIENT_TRUST_LIABILITY",
  "PAYROLL_WAGES_EXPENSE",
  "PAYROLL_TAX_EXPENSE",
  "PAYROLL_TAX_LIABILITY",
  "PAYROLL_DEDUCTION_LIABILITY",
];

const ACCOUNT_TYPE_LABELS = {
  ASSET: "Asset",
  LIABILITY: "Liability",
  EQUITY: "Equity",
  REVENUE: "Revenue",
  EXPENSE: "Expense",
  COGS: "Cost of Goods Sold",
};

const toCaption = (value) => String(value || "")
  .toLowerCase()
  .split("_")
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

class AccountItem extends React.Component {
  constructor(props) {
    super(props);
    this.props.loading(true);
    this.state = {
      data: this.mapPropsToState(props),
      accounts: [],
      modal: {
        error: { show: false },
        delete: { show: false },
        businessExpense: { show: false },
      },
    };

    this.props.pageInfo({ title: "Account", breadcrumb: "[New Account]" });
  }

  mapPropsToState = (props) => {
    const {
      uuid,
      accountNumber,
      name,
      type,
      subtype,
      purpose,
      startingBalance,
      parentUuid,
      description,
      active,
      meta,
    } = props;

    return {
      uuid,
      accountNumber,
      name,
      type: type || "ASSET",
      subtype,
      purpose,
      startingBalance: startingBalance ?? 0,
      parentUuid,
      description,
      active: active ?? true,
      meta,
    };
  };

  get uuid() {
    return this.props.params.uuid;
  }

  currentAccountUuid = () => {
    if (isGuid(this.state.data?.uuid)) return this.state.data.uuid;
    if (isGuid(this.uuid)) return this.uuid;
    return null;
  };

  accountLabel = (account) => {
    const number = String(account?.accountNumber || "").trim();
    const name = String(account?.name || "").trim();
    if (number && name) return `${number} - ${name}`;
    return name || number || account?.uuid || "";
  };

  loadAccounts = async () => {
    const accounts = await this.props.getAccount(null);
    this.setState({ accounts: accounts || [] });
  };

  loadData = async () => {
    this.props.loading(true);

    if (isGuid(this.uuid)) {
      const result = await this.props.getAccount(this.uuid);
      const item = result?.item || result;

      this.setState({ data: { ...item } });

      const caption = item?.name || item?.accountNumber || item?.uuid;
      await this.props.pageInfo({ title: `Account (${caption})`, breadcrumb: `${caption}` });
    }

    this.props.loading(false);
  };

  componentDidMount = () => {
    setTimeout(async () => {
      await this.loadAccounts();
      await this.loadData();
    }, 30);
  };

  componentDidUpdate = (oldProps) => {
    if (oldProps?.params?.uuid !== this.props?.params?.uuid) this.componentDidMount();
  };

  onChange = (e) => {
    const { name, value } = e.target;
    this.setState((prev) => ({
      data: {
        ...prev.data,
        [name]: value,
      },
    }));
  };

  onCheckChange = (e) => {
    const { name, checked } = e.target;
    this.setState((prev) => ({
      data: {
        ...prev.data,
        [name]: checked,
      },
    }));
  };

  get typeOptions() {
    return ACCOUNT_TYPES.map((type) => ({ value: type, label: ACCOUNT_TYPE_LABELS[type] || toCaption(type) }));
  }

  get selectedTypeOption() {
    return this.typeOptions.find((option) => option.value === (this.state.data?.type || "ASSET")) || this.typeOptions[0];
  }

  onTypeChange = (option) => {
    this.onChange({ target: { name: "type", value: option?.value || "ASSET" } });
  };

  get parentOptions() {
    const currentUuid = this.currentAccountUuid();
    return (this.state.accounts || [])
      .filter((account) => account?.uuid && account.uuid !== currentUuid)
      .map((account) => ({
        value: account.uuid,
        label: this.accountLabel(account),
      }));
  }

  get selectedParentOption() {
    const parentUuid = this.state.data?.parentUuid;
    if (!isGuid(parentUuid)) return null;
    return this.parentOptions.find((option) => option.value === parentUuid) || null;
  }

  onParentChange = (option) => {
    this.onChange({ target: { name: "parentUuid", value: option?.value || null } });
  };

  get purposeOptions() {
    return ACCOUNT_PURPOSES.map((purpose) => ({ value: purpose, label: toCaption(purpose) }));
  }

  get selectedPurposeOption() {
    const purpose = this.state.data?.purpose;
    if (!purpose) return null;
    return this.purposeOptions.find((option) => option.value === purpose) || null;
  }

  onPurposeChange = (option) => {
    this.onChange({ target: { name: "purpose", value: option?.value || null } });
  };

  showError = (message) => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: {
          title: "Account error",
          body: message,
          show: true,
        },
      },
    }));
  };

  onSaveClick = async () => {
    const data = this.state.data || {};

    if (!String(data.name || "").trim()) {
      this.showError("Account name is required.");
      return;
    }

    if (!ACCOUNT_TYPES.includes(data.type)) {
      this.showError("Account type is required.");
      return;
    }

    if (isGuid(data.parentUuid) && data.parentUuid === this.currentAccountUuid()) {
      this.showError("Account cannot be its own parent.");
      return;
    }

    this.props.loading(true);

    const payload = {
      ...data,
      uuid: isGuid(this.uuid) ? this.uuid : uuidv4(),
      parentUuid: isGuid(data.parentUuid) ? data.parentUuid : null,
      startingBalance: Number(data.startingBalance || 0),
      active: data.active ?? true,
    };

    const saved = await this.props.saveAccount(payload);

    if (saved?.success === false) {
      this.props.loading(false);
      this.showError(saved?.error?.message || "The account could not be saved.");
      return;
    }

    const id = saved?.uuid ?? payload.uuid;
    window.location.href = `/dashboard/accounting/accounts/${id}`;
  };

  onDeleteClick = () => {
    const caption = this.state.data?.name || this.state.data?.accountNumber || this.uuid;

    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: {
          title: "Delete account",
          body: `This action will permanently delete the account ${caption}.`,
          show: true,
        },
      },
    }));
  };

  onDeleteOkModal = async () => {
    await this.props.destroyAccount(this.state.data?.uuid || this.uuid);
    this.onDeleteHideModal();
    this.props.navigate("/dashboard/accounting/accounts");
  };

  onDeleteHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        delete: { show: false },
      },
    }));
  };

  onErrorHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        error: { show: false },
      },
    }));
  };

  get newItems() {
    return [
      {
        caption: "Business Expense",
        onClick: this.onBusinessExpenseClick,
      },
    ];
  }

  onBusinessExpenseClick = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        businessExpense: { show: true },
      },
    }));
  };

  onBusinessExpenseHideModal = () => {
    this.setState((prev) => ({
      modal: {
        ...prev.modal,
        businessExpense: { show: false },
      },
    }));
  };

  onBusinessExpenseSaved = () => {
    this.onBusinessExpenseHideModal();
  };

  render() {
    const d = this.state.data || {};

    return (
      <>
        <Col md="8" className="float-right text-end">
          <ConditionalDropdown
            className="ms-sm-1"
            enabledVariant="outline-primary"
            display={isGuid(this.currentAccountUuid())}
            enabled
            items={this.newItems}
            icon="plus"
          >
            New
          </ConditionalDropdown>

          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Save Account"
            display={true}
            enabled={true}
            onEnabledClick={this.onSaveClick}
            icon="save"
          >
            Save
          </ConditionalButton>

          <ConditionalButton
            enabledVariant="outline-primary"
            tooltip="Delete Account"
            display={true}
            enabled={isGuid(this.uuid)}
            onEnabledClick={this.onDeleteClick}
            icon="bin"
          >
            Delete
          </ConditionalButton>
        </Col>

        <Col xs="12">
          <Row className="">
            <Col xs="12" md="4" xl="3">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="accountNumber"
                  value={d?.accountNumber || ""}
                  onChange={this.onChange}
                />
                <Form.Label>ACCOUNT NUMBER</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="8" xl="5">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="name"
                  value={d?.name || ""}
                  onChange={this.onChange}
                />
                <Form.Label>NAME</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6" xl="2">
              <div className="mb-3 top-label">
                <Select
                  inputId="type"
                  classNamePrefix="react-select"
                  isClearable={false}
                  options={this.typeOptions}
                  value={this.selectedTypeOption}
                  onChange={this.onTypeChange}
                />
                <Form.Label>TYPE</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6" xl="2">
              <div className="mb-3 top-label">
                <NumberFormat
                  name="startingBalance"
                  id="startingBalance"
                  value={d?.startingBalance ?? ""}
                  thousandSeparator
                  allowNegative
                  inputMode="decimal"
                  decimalScale={2}
                  prefix="$"
                  className="text-end form-control mb-2"
                  onValueChange={({ floatValue }) => {
                    this.onChange({ target: { name: "startingBalance", value: floatValue ?? 0 } });
                  }}
                />
                <Form.Label>STARTING BALANCE</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6">
              <div className="mb-3 top-label">
                <Form.Control
                  type="text"
                  name="subtype"
                  value={d?.subtype || ""}
                  onChange={this.onChange}
                />
                <Form.Label>SUBTYPE</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6">
              <div className="mb-3 top-label">
                <Select
                  inputId="purpose"
                  classNamePrefix="react-select"
                  isClearable
                  placeholder="(No posting purpose)"
                  options={this.purposeOptions}
                  value={this.selectedPurposeOption}
                  onChange={this.onPurposeChange}
                />
                <Form.Label>POSTING PURPOSE</Form.Label>
              </div>
            </Col>

            <Col xs="12" md="6">
              <div className="mb-3 top-label">
                <Select
                  inputId="parentUuid"
                  classNamePrefix="react-select"
                  isClearable
                  placeholder="(No parent account)"
                  options={this.parentOptions}
                  value={this.selectedParentOption}
                  onChange={this.onParentChange}
                />
                <Form.Label>
                  PARENT ACCOUNT
                  {isGuid(d?.parentUuid) && (
                    <a
                      href={`/dashboard/accounting/accounts/${d.parentUuid}`}
                      className="ms-1 text-muted"
                      title="Open parent account"
                    >
                      <CsLineIcons icon="arrow-bottom-left" size="10" />
                    </a>
                  )}
                </Form.Label>
              </div>
            </Col>

            <Col xs="12">
              <div className="mb-3 top-label">
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="description"
                  value={d?.description || ""}
                  onChange={this.onChange}
                />
                <Form.Label>DESCRIPTION</Form.Label>
              </div>
            </Col>

            <Col xs="12">
              <Form.Check
                type="switch"
                id="account-active"
                name="active"
                label="Active"
                checked={Boolean(d?.active)}
                onChange={this.onCheckChange}
              />
            </Col>

            <AccountEntries accountUuid={this.currentAccountUuid()} />
          </Row>
        </Col>

        <ErrorModal
          show={this.state.modal.error.show}
          title={this.state.modal.error.title}
          body={this.state.modal.error.body}
          buttons={[
            {
              default: true,
              caption: "ok",
              onEnabledClick: this.onErrorHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        ></ErrorModal>

        <DeleteModal
          show={this.state.modal.delete.show}
          title={this.state.modal.delete.title}
          body={this.state.modal.delete.body}
          buttons={[
            {
              caption: "ok",
              onEnabledClick: this.onDeleteOkModal,
              variant: "primary",
              display: true,
              enabled: true,
            },
            {
              default: true,
              caption: "cancel",
              onEnabledClick: this.onDeleteHideModal,
              variant: "secondary",
              display: true,
              enabled: true,
            },
          ]}
        ></DeleteModal>

        <BusinessExpenseModal
          show={this.state.modal.businessExpense.show}
          defaultAccountingAccountUuid={this.currentAccountUuid()}
          onCancel={this.onBusinessExpenseHideModal}
          onSaved={this.onBusinessExpenseSaved}
        />
      </>
    );
  }
}

AccountItem.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default withNavigation(
  connect(mapStateToProps, {
    pageInfo,
    loading,
    getAccount,
    saveAccount,
    destroyAccount,
  })(AccountItem)
);
